import { pluck } from 'ramda'
import { actions, createMachine, InterpreterFrom } from 'xstate'
import type { Campaign, Size } from '../types'
import { donationMachine } from './donationMachine'
import { productMachine } from './productMachine'
import { validationMachine } from './validationMachine'

const { choose, assign, sendTo } = actions

type Product = { size: Size }

type AddToCartContext = {
  campaign: Campaign
  product?: Product
  donation?: number
}

type SubmitEvent = {
  type: 'SUBMIT'
}

type SetProductEvent = {
  type: 'SET_PRODUCT'
  product?: Product
}

type SetDonationEvent = {
  type: 'SET_DONATION'
  donation?: number
}

export type AddToCartEvent = SubmitEvent | SetProductEvent | SetDonationEvent

type AddToCartTypestate = {
  value: 'active'
  context: AddToCartContext
}

export const addToCartMachine = createMachine<
  AddToCartContext,
  AddToCartEvent,
  AddToCartTypestate
>(
  {
    id: 'addToCart',
    initial: 'active',
    invoke: [
      {
        id: 'donation',
        src: donationMachine,
        data: {
          presets: (context: AddToCartContext) =>
            pluck('price', context.campaign.donation.pricePresets),
        },
      },
      {
        id: 'product',
        src: productMachine,
      },
      { id: 'validation', src: validationMachine },
    ],

    states: {
      active: {},
    },

    on: {
      SUBMIT: [
        {
          actions: choose([
            {
              cond: 'invalid',
              actions: sendTo('validation', {
                type: 'INVALIDATE',
                errorMessage: 'Please select a size.',
              }),
            },
            {
              actions: [
                'addToCart',
                sendTo('donation', { type: 'RESET' }),
                sendTo('product', { type: 'UNSET' }),
              ],
            },
          ]),
        },
      ],
      SET_PRODUCT: {
        actions: assign({ product: (campaign, { product }) => product }),
      },
      SET_DONATION: {
        actions: assign({ donation: (campaign, { donation }) => donation }),
      },
    },
    predictableActionArguments: true,
  },

  {
    actions: {
      addToCart: ({ campaign, product, donation }) => {
        if (!product) return // can't happen, but TypeScript doesn't know it

        addToCart(campaign, product.size, donation || 0)
      },
    },

    guards: {
      valid: (context) => !!context.product,
      invalid: (context) => !context.product,
    },
  }
)

function addToCart(campaign: Campaign, size: Size, donationAmount: number) {
  const { offer, donation } = campaign
  const { itemOffered } = offer

  window.CartiganJS.addToCart([
    {
      productId: itemOffered.sku,
      name: itemOffered.name,
      imageUrl: itemOffered.cartImage.url,
      basePriceInCents: offer.price * 100,
      variantSelections: [
        {
          variantName: 'size',
          name: size.name,
          variantId: 'size',
          optionId: size.id,
        },
      ],
    },
    {
      productId: donation.sku,
      name: donation.name,
      imageUrl: donation.cartImage.url,
      basePriceInCents: donationAmount * 100,
      hasAdjustablePrice: true,
      dependentOf: itemOffered.sku,
      additionalCheckoutHTML: campaign.checkoutText?.addressFormAdditionalHTML,
    },
  ])
}

export type AddToCartInterpreter = InterpreterFrom<typeof addToCartMachine>
